import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { LOGO_URL, LOGO_TRANSPARENT } from "../data/mock";
import { toast } from "sonner";
import { trackEvent } from "../lib/analytics";
import { PaymentMarks } from "./PaymentMarks";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SignUpSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    address: "",
    phone: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(null); // null | "register" | "pay"

  // Capture ?ref=<signup_id> for referral attribution
  const [referredBy] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get("ref");
    } catch {
      return null;
    }
  });

  // Fire referral_link_visit analytics event once on mount if ?ref= is present
  useEffect(() => {
    if (referredBy) {
      trackEvent("referral_link_visit", { ref_id: referredBy });
    }
  }, [referredBy]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const submit = async (mode) => {
    if (!formData.name || !formData.email || !formData.address || !formData.phone) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(mode);
    try {
      // Step 1: Create signup
      const signupRes = await axios.post(`${API}/signups`, {
        ...formData,
        referred_by: referredBy || null,
      });
      const signupId = signupRes.data.id;
      trackEvent("signup_form_submit", {
        signup_id: signupId,
        has_referral: Boolean(referredBy),
        mode,
      });

      if (mode === "register") {
        trackEvent("register_interest", { signup_id: signupId });
        setSubmitted(true);
        return;
      }

      // mode === "pay" — create Stripe checkout and redirect
      const originUrl = window.location.origin;
      const checkoutRes = await axios.post(`${API}/checkout/session`, {
        origin_url: originUrl,
        signup_id: signupId,
      });
      if (checkoutRes.data.url) {
        trackEvent("checkout_initiated", { signup_id: signupId, currency: "AED", value: 99 });
        window.location.href = checkoutRes.data.url;
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your details and try again.");
    } finally {
      setLoading(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Default form submission (Enter key) → "Sign Up & Pay"
    submit("pay");
  };

  return (
    <section
      id="signup"
      className="py-24 lg:py-32 relative overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-[#0C2D48]" />
      <div className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: "radial-gradient(circle at 20% 50%, #16C79A 0%, transparent 50%), radial-gradient(circle at 80% 50%, #16C79A 0%, transparent 50%)",
        }}
      />
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
      />

      {/* Floating mascot - bottom left */}
      <motion.img
        src={LOGO_TRANSPARENT}
        alt=""
        aria-hidden="true"
        className="absolute bottom-8 left-6 w-36 opacity-[0.08] -rotate-6 pointer-events-none select-none hidden lg:block"
        animate={{ y: [5, -5, 5], rotate: [-6, -3, -6] }}
        transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
      />
      {/* Floating mascot - top right */}
      <motion.img
        src={LOGO_TRANSPARENT}
        alt=""
        aria-hidden="true"
        className="absolute top-10 right-8 w-32 opacity-[0.06] rotate-12 pointer-events-none select-none hidden lg:block"
        animate={{ y: [-7, 7, -7] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-[#16C79A] text-sm font-bold uppercase tracking-[0.2em] mb-4">
              Get Started
            </span>
            <h2 className="text-4xl lg:text-5xl font-extrabold text-white mb-6 leading-tight">
              Ready for{" "}
              <span className="text-[#16C79A]">Spotless</span> Bins?
            </h2>
            <p className="text-lg text-white/70 leading-relaxed mb-8 max-w-md">
              Join the growing number of happy Dubai customers who never worry about dirty
              bins again. Sign up today and we'll have your bins sparkling
              in no time.
            </p>

            {/* Features List */}
            <div className="space-y-4">
              {[
                "Service every 2 weeks with no contracts - cancel anytime",
                "Easy payment system — take advantage of our 99dhs per month launch price! Valid til September.",
                "Fully insured & licensed team",
                "Eco-friendly, biodegradable cleaning",
              ].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#16C79A]/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 size={14} className="text-[#16C79A]" />
                  </div>
                  <span className="text-white/80 text-[15px]">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="bg-white/[0.08] backdrop-blur-2xl rounded-3xl p-8 lg:p-10 border border-white/10">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 rounded-full bg-[#16C79A]/20 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 size={40} className="text-[#16C79A]" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">
                    You're All Signed Up!
                  </h3>
                  <p className="text-white/60">
                    We'll be in touch within 24 hours to arrange your first
                    clean.
                  </p>
                  <img
                    src={LOGO_TRANSPARENT}
                    alt="Steamers"
                    className="w-32 mx-auto mt-8 opacity-60"
                  />
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-white mb-2">
                      Register Your Interest
                    </h3>
                    <p className="text-white/50 text-sm">
                      Drop your details below and we'll get straight back to you! ...or if you're ready to get started, just drop your details and click 'Sign Up & Pay' - we'll be in touch to confirm a start date.
                      <br /><br />
                      Our <a href="https://wa.me/971502856909" target="_blank" rel="noopener noreferrer" className="text-[#25D366] hover:underline">WhatsApp</a> is always open too - we're real people from the UK and we'd love to hear from you!
                    </p>
                  </div>

                  <div>
                    <label className="block text-white/70 text-sm font-medium mb-1.5">
                      Full Name *
                    </label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Smith"
                      className="bg-white/10 border-white/10 text-white placeholder:text-white/30 focus:border-[#16C79A] focus:ring-[#16C79A]/20 rounded-xl h-12"
                    />
                  </div>

                  <div>
                    <label className="block text-white/70 text-sm font-medium mb-1.5">
                      Email Address *
                    </label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@email.com"
                      className="bg-white/10 border-white/10 text-white placeholder:text-white/30 focus:border-[#16C79A] focus:ring-[#16C79A]/20 rounded-xl h-12"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/70 text-sm font-medium mb-1.5">
                        Address *
                      </label>
                      <Input
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="Your address"
                        className="bg-white/10 border-white/10 text-white placeholder:text-white/30 focus:border-[#16C79A] focus:ring-[#16C79A]/20 rounded-xl h-12"
                      />
                    </div>
                    <div>
                      <label className="block text-white/70 text-sm font-medium mb-1.5">
                        Phone *
                      </label>
                      <Input
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="07123 456789"
                        className="bg-white/10 border-white/10 text-white placeholder:text-white/30 focus:border-[#16C79A] focus:ring-[#16C79A]/20 rounded-xl h-12"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3 mt-2">
                    <Button
                      type="button"
                      onClick={() => submit("register")}
                      disabled={Boolean(loading)}
                      data-testid="register-interest-btn"
                      className="bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold py-6 rounded-xl text-base transition-all duration-300 hover:shadow-lg hover:shadow-white/10 disabled:opacity-50"
                    >
                      {loading === "register" ? (
                        <>
                          <Loader2 size={18} className="animate-spin mr-2" />
                          Submitting...
                        </>
                      ) : (
                        "Register Your Interest"
                      )}
                    </Button>

                    <Button
                      type="submit"
                      disabled={Boolean(loading)}
                      data-testid="signup-pay-btn"
                      className="bg-[#16C79A] hover:bg-[#12a87e] text-[#0C2D48] font-bold py-6 rounded-xl text-base transition-all duration-300 hover:shadow-lg hover:shadow-[#16C79A]/25 disabled:opacity-50"
                    >
                      {loading === "pay" ? (
                        <>
                          <Loader2 size={18} className="animate-spin mr-2" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Send size={16} className="mr-2" />
                          Sign Up &amp; Pay
                        </>
                      )}
                    </Button>
                  </div>

                  <p className="text-white/30 text-xs text-center mt-4">
                    We respect your privacy. No spam, ever.
                  </p>

                  {/* Apple Pay / Google Pay marks */}
                  <div className="flex items-center justify-center gap-2 pt-1">
                    <span className="text-white/40 text-[11px] uppercase tracking-wider font-semibold">
                      Pay with
                    </span>
                    <PaymentMarks variant="pill" size="xs" />
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
